/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import {
  AlfrescoViewerComponent,
  ContentService,
  NodeCommentsComponent,
  NodeDownloadDirective,
  NodesApiService,
  TagService,
  UploadService
} from '@alfresco/adf-content-services';
import { IconComponent, NotificationService } from '@alfresco/adf-core';
import { DocumentPermissionsComponent } from '../permissions/document-permissions.component';
import { PdfStudioComponent } from '../pdf-studio/pdf-studio.component';
import { WatermarkOverlayComponent } from '../watermark/watermark-overlay.component';
import { WatermarkPrintService } from '../watermark/watermark-print.service';
import { DocumentHistoryComponent } from '../history/document-history.component';
import { DocumentPropertiesComponent } from './document-properties.component';
import { DynamicExtensionComponent } from '@alfresco/adf-extensions';
import { TranslateModule, TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AppExtensionService, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { AppStore, ManageVersionsAction, ResetSelectionAction, SetSelectedNodesAction, ShareNodeAction } from '@alfresco/aca-shared/store';
import { Observable, Subscription, firstValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { PreviewRedirectService } from './preview-redirect.service';
import { OcrService } from '../ocr/ocr.service';
import { OcrPipelineService } from '../ocr/ocr-pipeline.service';
import { AssistantService, InsightResult } from '../assistant/assistant.service';
import { HttpClient } from '@angular/common/http';

type DocTab = 'view' | 'properties' | 'permissions' | 'history';

/**
 * In-layout document view — the page shown when a file is opened.
 *
 * Replaces the full-screen ADF viewer overlay with a content-area page laid out
 * like a document detail screen: a header (file name + folder path + actions),
 * tabs (VIEW / PROPERTIES / PERMISSIONS / HISTORY), and for VIEW the preview (with its zoom /
 * rotate controls) on the left and an information column on the right — title,
 * dates, author, tags and comments.
 *
 * Reached via `/document/:id` (see PreviewRedirectService, which redirects the
 * stock viewer navigation here).
 */
@Component({
  selector: 'app-file-preview-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
    TranslateModule,
    IconComponent,
    DynamicExtensionComponent,
    AlfrescoViewerComponent,
    NodeCommentsComponent,
    NodeDownloadDirective,
    WatermarkOverlayComponent,
    PdfStudioComponent,
    DocumentPermissionsComponent,
    DocumentHistoryComponent,
    DocumentPropertiesComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-docview" [class.aca-docview--wide]="activeTab === 'permissions' || activeTab === 'history'">
      <!-- Header: file identity + actions -->
      <header class="aca-docview__bar">
        <!-- the trail runs the full width above the title, as the reference has
             it, so the type icon sits beside the name and nothing indents the
             breadcrumb away from the left edge -->
        <div class="aca-docview__id">
          <nav class="aca-docview__crumb" [attr.aria-label]="'DMS.HISTORY.F_WHERE' | translate">
            <mat-icon class="aca-docview__crumb-home">folder_open</mat-icon>
            <ng-container *ngFor="let part of trail; trackBy: trackCrumb">
              <button type="button" class="aca-docview__crumb-link" *ngIf="part.id; else crumbText" (click)="openFolder(part)">
                {{ part.name }}
              </button>
              <ng-template #crumbText
                ><span class="aca-docview__crumb-plain">{{ part.name }}</span></ng-template
              >
              <mat-icon class="aca-docview__crumb-sep">chevron_right</mat-icon>
            </ng-container>
            <button type="button" class="aca-docview__crumb-link aca-docview__crumb-here" (click)="openSelf()">{{ node?.name }}</button>
          </nav>

          <div class="aca-docview__titlerow">
            <mat-icon class="aca-docview__type-icon">{{ typeIcon }}</mat-icon>
            <h1 class="aca-docview__name" [title]="node?.name">{{ node?.name || '…' }}</h1>
          </div>
        </div>
        <div class="aca-docview__bar-actions">
          <button type="button" class="aca-docview__act" (click)="share()" [disabled]="!node" [attr.aria-label]="'DMS.UI.9CC1C3' | translate">
            <mat-icon>link</mat-icon><span>{{ 'DMS.UI.9CC1C3' | translate }}</span>
          </button>
          <button
            type="button"
            class="aca-docview__act"
            [adfNodeDownload]="nodeEntry"
            [disabled]="!node"
            [attr.aria-label]="'DMS.UI.5C6233' | translate"
          >
            <mat-icon>download</mat-icon><span>{{ 'DMS.UI.5C6233' | translate }}</span>
          </button>
          <button type="button" class="aca-docview__act" (click)="print()" [disabled]="!nodeId" [attr.aria-label]="'DMS.UI.E9E1CF' | translate">
            <mat-icon>print</mat-icon><span>{{ 'DMS.UI.E9E1CF' | translate }}</span>
          </button>
          <button
            mat-icon-button
            [matMenuTriggerFor]="moreMenu"
            [matTooltip]="'DMS.UI.76A0B4' | translate"
            [attr.aria-label]="'DMS.UI.76A0B4' | translate"
            [disabled]="!node"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
          <span class="aca-docview__bar-divider"></span>
          <button
            mat-icon-button
            (click)="openNative()"
            [matTooltip]="'DMS.UI.A50CFE' | translate"
            [attr.aria-label]="'DMS.UI.A50CFE' | translate"
            [disabled]="!nodeId"
          >
            <mat-icon>open_in_full</mat-icon>
          </button>
          <button
            mat-icon-button
            class="aca-docview__close"
            (click)="close()"
            [matTooltip]="'DMS.UI.1B6D61' | translate"
            [attr.aria-label]="'DMS.UI.1B6D61' | translate"
          >
            <mat-icon>close</mat-icon>
          </button>

          <!-- Same actions ACA shows in the file's context menu / viewer "more" -->
          <mat-menu #moreMenu="matMenu" class="aca-docview__more-menu">
            <button mat-menu-item (click)="updateDocument()" [disabled]="!nodeId">
              <adf-icon [value]="'upload_file'" />
              <span>{{ 'DMS.UPLOAD.UPDATE_MENU' | translate }}</span>
            </button>
            <button mat-menu-item (click)="downloadText()" [disabled]="!nodeId">
              <adf-icon [value]="'text_snippet'" />
              <span>{{ 'DMS.UI.OCR_DOWNLOAD' | translate }}</span>
            </button>
            <button mat-menu-item (click)="reindexOcr()" [disabled]="!nodeId || reindexBusy || !canOcr">
              <adf-icon [value]="'manage_search'" />
              <span>{{ 'DMS.UI.OCR_REINDEX' | translate }}</span>
            </button>
            <mat-divider />
            <ng-container *ngFor="let item of moreActions$ | async; trackBy: trackActionId">
              <mat-divider *ngIf="item.type === 'separator'" />

              <ng-container *ngIf="item.type === 'menu'">
                <button mat-menu-item [matMenuTriggerFor]="childMenu">
                  <adf-icon [value]="item.icon" />
                  <span>{{ item.title | translate }}</span>
                </button>
                <mat-menu #childMenu="matMenu">
                  <button mat-menu-item *ngFor="let child of item.children; trackBy: trackActionId" (click)="runAction(child)">
                    <adf-icon [value]="child.icon" />
                    <span>{{ child.title | translate }}</span>
                  </button>
                </mat-menu>
              </ng-container>

              <!-- Custom actions (e.g. Edit Offline / Favorite toggles) render their own component -->
              <adf-dynamic-component *ngIf="item.type === 'custom'" [data]="item.data" [id]="item.component" />

              <button
                mat-menu-item
                *ngIf="item.type !== 'separator' && item.type !== 'menu' && item.type !== 'custom'"
                (click)="runAction(item)"
                [disabled]="item.disabled"
              >
                <adf-icon [value]="item.icon" />
                <span>{{ item.title | translate }}</span>
              </button>
            </ng-container>
          </mat-menu>
        </div>
      </header>

      <!-- a document that cannot be opened says why, instead of leaving "…" -->
      <aca-page-state
        *ngIf="loadError"
        state="error"
        [icon]="loadError === 'forbidden' ? 'lock' : loadError === 'missing' ? 'search_off' : 'error_outline'"
        [title]="'DMS.DOCVIEW.' + loadError.toUpperCase() + '_TITLE' | translate"
        [message]="'DMS.DOCVIEW.' + loadError.toUpperCase() + '_HINT' | translate"
      />

      <!-- Body: the document stays on the left whichever tab is open, so a
           reader never has to leave the page to look at what they are
           describing. The tabs sit with the panel they switch. -->
      <div class="aca-docview__body" *ngIf="!loadError">
        <main class="aca-docview__main">
          <div class="aca-docview__preview" [class.aca-docview__preview--studio]="isPdf">
            <app-pdf-studio
              *ngIf="nodeId && viewerVisible && isPdf"
              [nodeId]="nodeId"
              [nodeName]="node?.name || ''"
              [canWrite]="canEditPdf"
              (saved)="onPdfSaved()"
            />
            <adf-alfresco-viewer
              *ngIf="nodeId && viewerVisible && !isPdf"
              [nodeId]="nodeId"
              [overlayMode]="false"
              [showToolbar]="false"
              [allowGoBack]="false"
              [allowFullScreen]="false"
            />
            <!-- the administrator's watermark, drawn over whatever the viewer
                 shows — the file itself is never touched -->
            <app-watermark-overlay *ngIf="nodeId" [docName]="node?.name || ''" />
          </div>
        </main>

        <aside class="aca-docview__side">
          <!-- Tabs. A tablist rather than a row of buttons, so that assistive
               technology announces which view is current, and so the arrow keys
               move between the three the way they do in any other tab strip. -->
          <div class="aca-docview__tabs" role="tablist" [attr.aria-label]="'DMS.UI.891BC7' | translate">
            <button
              *ngFor="let tab of tabs"
              type="button"
              role="tab"
              class="aca-docview__tab"
              [class.aca-docview__tab--active]="activeTab === tab.id"
              [id]="'aca-docview-tab-' + tab.id"
              [attr.aria-controls]="'aca-docview-panel-' + tab.id"
              [attr.aria-selected]="activeTab === tab.id"
              [attr.tabindex]="activeTab === tab.id ? 0 : -1"
              (click)="activeTab = tab.id"
              (keydown)="onTabKeys($event)"
            >
              <mat-icon>{{ tab.icon }}</mat-icon
              >{{ tab.label | translate }}
            </button>
          </div>

          <!-- DOCUMENT: what the file is, read from the node itself -->
          <div
            class="aca-docview__sidepanel"
            *ngIf="activeTab === 'view' && node"
            role="tabpanel"
            id="aca-docview-panel-view"
            aria-labelledby="aca-docview-tab-view"
          >
            <!-- who and what the file is, the way the reference lays it out -->
            <div class="aca-docview__filecard">
              <span class="aca-docview__filetile"
                ><mat-icon>{{ typeIcon }}</mat-icon></span
              >
              <span class="aca-docview__filetext">
                <span class="aca-docview__filename">{{ node?.name }}</span>
                <span class="aca-docview__filesub">{{ mimeLabel }} · {{ sizeLabel }}</span>
              </span>
              <button type="button" class="aca-docview__primary" (click)="manageVersions()">
                <mat-icon>layers</mat-icon>{{ 'DMS.UI.B4081D' | translate }}
              </button>
            </div>

            <!-- the assistant's own summary, shown only when an administrator
                 turned the feature on; the button reads the indexed text the
                 caller may already read -->
            <section class="aca-docview__block" *ngIf="assistantStatus()?.insights">
              <h3 class="aca-docview__blocktitle"><mat-icon>auto_awesome</mat-icon>{{ 'DMS.ASSISTANT.SUMMARY_TITLE' | translate }}</h3>
              <p class="aca-docview__aitext" *ngIf="summary?.summary">{{ summary!.summary }}</p>
              <p class="aca-docview__hint" *ngIf="summary?.summary">
                {{ 'DMS.ASSISTANT.SUMMARY_BY' | translate: { provider: summary!.provider, model: summary!.model } }}
              </p>
              <p class="aca-docview__hint" *ngIf="!summary?.summary">{{ 'DMS.ASSISTANT.SUMMARY_HINT' | translate }}</p>
              <button type="button" class="aca-docview__primary" (click)="summarize()" [disabled]="summaryBusy">
                <mat-icon>{{ summary?.summary ? 'refresh' : 'auto_awesome' }}</mat-icon>
                {{
                  (summaryBusy ? 'DMS.ASSISTANT.SUMMARY_BUSY' : summary?.summary ? 'DMS.ASSISTANT.SUMMARY_AGAIN' : 'DMS.ASSISTANT.SUMMARIZE')
                    | translate
                }}
              </button>
            </section>

            <section class="aca-docview__block">
              <h3 class="aca-docview__blocktitle">{{ 'DMS.PREVIEW.GENERAL' | translate }}</h3>

              <dl class="aca-docview__rows">
                <dt><mat-icon>layers</mat-icon>{{ 'DMS.UI.0A2379' | translate }}</dt>
                <dd>
                  <span class="aca-docview__version-badge">{{ versionLabel }}</span>
                  <button type="button" class="aca-docview__link" (click)="activeTab = 'history'">
                    {{ 'DMS.UI.3A6843' | translate }}
                  </button>
                </dd>

                <dt><mat-icon>event</mat-icon>{{ 'DMS.UI.DC1FAC' | translate }}</dt>
                <dd>{{ (node?.modifiedAt | acaDate: 'd MMMM y HH:mm') || '—' }}</dd>

                <dt><mat-icon>schedule</mat-icon>{{ 'DMS.UI.58DBAB' | translate }}</dt>
                <dd>{{ (node?.createdAt | acaDate: 'd MMMM y HH:mm') || '—' }}</dd>

                <dt><mat-icon>person</mat-icon>{{ 'DMS.UI.EB455F' | translate }}</dt>
                <dd class="aca-docview__who">
                  <span class="aca-docview__avatar">{{ initial(node?.createdByUser?.displayName) }}</span>
                  {{ node?.createdByUser?.displayName || '—' }}
                </dd>

                <dt><mat-icon>edit</mat-icon>{{ 'DMS.UI.BD0B2D' | translate }}</dt>
                <dd class="aca-docview__who">
                  <span class="aca-docview__avatar">{{ initial(node?.modifiedByUser?.displayName) }}</span>
                  {{ node?.modifiedByUser?.displayName || '—' }}
                </dd>

                <dt><mat-icon>folder_open</mat-icon>{{ 'DMS.HISTORY.F_WHERE' | translate }}</dt>
                <dd>
                  <button type="button" class="aca-docview__link" (click)="openFolder(trail[trail.length - 1])" *ngIf="trail.length">
                    {{ breadcrumb }}
                  </button>
                  <span *ngIf="!trail.length">—</span>
                </dd>

                <dt><mat-icon>image</mat-icon>{{ 'DMS.HISTORY.F_TYPE' | translate }}</dt>
                <dd>{{ mimeLabel }}</dd>

                <dt><mat-icon>description</mat-icon>{{ 'DMS.HISTORY.F_SIZE' | translate }}</dt>
                <dd>{{ sizeLabel }}</dd>
              </dl>
            </section>

            <section class="aca-docview__block" *ngIf="nodeId">
              <h3 class="aca-docview__blocktitle"><mat-icon>sell</mat-icon>{{ 'DMS.UI.792FF6' | translate }}</h3>
              <div class="aca-docview__tags">
                <span class="aca-docview__tag" *ngFor="let t of tags">
                  {{ t.entry.tag }}
                  <button
                    class="aca-docview__tag-remove"
                    (click)="removeTag(t)"
                    [matTooltip]="'DMS.UI.BDD0C7' | translate"
                    [attr.aria-label]="'DMS.UI.BDD0C7' | translate"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </span>
                <span class="aca-docview__tags-empty" *ngIf="!tags.length">{{ 'DMS.UI.8E116C' | translate }}</span>
              </div>
              <div class="aca-docview__tag-add">
                <input
                  class="aca-docview__tag-input"
                  [(ngModel)]="newTag"
                  (keyup.enter)="addTag()"
                  [placeholder]="'DMS.UI.631400' | translate"
                  [disabled]="savingTag"
                />
                <button mat-icon-button (click)="addTag()" [disabled]="!newTag.trim() || savingTag" [matTooltip]="'DMS.UI.6C89F3' | translate">
                  <mat-icon>add</mat-icon>
                </button>
              </div>
              <p class="aca-docview__hint">{{ 'DMS.UI.C74415' | translate }}</p>
            </section>

            <section class="aca-docview__block" *ngIf="nodeId">
              <h3 class="aca-docview__blocktitle"><mat-icon>chat_bubble_outline</mat-icon>{{ 'DMS.UI.5A1470' | translate }}</h3>
              <adf-node-comments [nodeId]="nodeId" />
            </section>
          </div>

          <!-- PROPERTIES: the metadata the repository lets a reader change -->
          <div
            class="aca-docview__sidepanel"
            *ngIf="activeTab === 'properties' && node"
            role="tabpanel"
            id="aca-docview-panel-properties"
            aria-labelledby="aca-docview-tab-properties"
          >
            <app-document-properties [node]="node" />
          </div>

          <!-- PERMISSIONS
             The list itself is ADF's: it owns reading the ACL, the inheritance
             toggle, the add-permission dialog and every write. What is added
             here is the chrome the reference asks for — a titled card, a
             labelled "add" button in place of ADF's bare icon, and the panel
             explaining how the two kinds of permission combine. -->
          <div
            class="aca-docview__sidepanel aca-docview__sidepanel--permissions"
            *ngIf="activeTab === 'permissions' && nodeId"
            role="tabpanel"
            id="aca-docview-panel-permissions"
            aria-labelledby="aca-docview-tab-permissions"
          >
            <div class="aca-perm__main">
              <app-document-permissions [nodeId]="nodeId" />
            </div>

            <aside class="aca-perm__about">
              <h3 class="aca-perm__abouttitle">
                <mat-icon>info_outline</mat-icon>
                {{ 'DMS.UI.F593AF' | translate }}
              </h3>

              <div class="aca-perm__kind">
                <span class="aca-perm__kindicon aca-perm__kindicon--inherit"><mat-icon>groups</mat-icon></span>
                <span class="aca-perm__kindtext">
                  <strong>{{ 'DMS.UI.A3D83C' | translate }}</strong>
                  {{ 'DMS.UI.93DE98' | translate }}
                </span>
              </div>

              <div class="aca-perm__kind">
                <span class="aca-perm__kindicon aca-perm__kindicon--direct"><mat-icon>person</mat-icon></span>
                <span class="aca-perm__kindtext">
                  <strong>{{ 'DMS.UI.E74C2D' | translate }}</strong>
                  {{ 'DMS.UI.5B1C4D' | translate }}
                </span>
              </div>

              <div class="aca-perm__order">
                <h4><mat-icon>description</mat-icon>{{ 'DMS.UI.E8B584' | translate }}</h4>
                <ol>
                  <li>{{ 'DMS.UI.205BB1' | translate }}</li>
                  <li>{{ 'DMS.UI.05611C' | translate }}</li>
                  <li>{{ 'DMS.UI.EFC6BF' | translate }}</li>
                </ol>
              </div>

              <a class="aca-perm__learn" [href]="helpUrl" target="_blank" rel="noopener">
                <mat-icon>help_outline</mat-icon>
                <span>{{ 'DMS.PERMISSIONS.LEARN_MORE' | translate }}</span>
                <mat-icon class="aca-perm__learngo">chevron_right</mat-icon>
              </a>
            </aside>
          </div>

          <!-- HISTORY / Version control (UI-20)
             The timeline, the summary above it and the details of whichever
             version is picked all live in a panel of their own; ADF's version
             manager has neither that shape nor those fields. -->
          <div
            class="aca-docview__sidepanel aca-docview__sidepanel--history"
            *ngIf="activeTab === 'history' && node"
            role="tabpanel"
            id="aca-docview-panel-history"
            aria-labelledby="aca-docview-tab-history"
          >
            <app-document-history [node]="node" (showPermissions)="activeTab = 'permissions'" />
          </div>
        </aside>
      </div>
    </div>
  `
})
export class FilePreviewPageComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly nodesApi = inject(NodesApiService);
  private readonly contentService = inject(ContentService);
  private readonly tagService = inject(TagService);
  private readonly uploadService = inject(UploadService);
  private readonly notification = inject(NotificationService);
  private readonly redirect = inject(PreviewRedirectService);
  private readonly watermarkPrint = inject(WatermarkPrintService);
  private readonly ocr = inject(OcrService);
  private readonly ocrPipeline = inject(OcrPipelineService);
  private readonly assistant = inject(AssistantService);
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store<AppStore>);
  private readonly actions$ = inject(Actions);
  private readonly appExtensions = inject(AppExtensionService);
  private readonly sub = new Subscription();

  nodeId: string | null = null;
  node: any = null;
  backUrl = '/all-documents';
  /** Set when the caller said where to return to; otherwise Close goes back through history. */
  private explicitBack = false;

  /** Why the document could not be opened — shown instead of an empty page. */
  loadError: 'forbidden' | 'missing' | 'failed' | null = null;
  activeTab: DocTab = 'view';

  /**
   * ADF's permission list, so the buttons in our own header can drive it. Its
   * add-permission dialog, its selection and its writes all stay ADF's; only
   * the chrome around them is ours.
   */

  /** Total versions — the ADF version list renders a count nowhere. */

  /** Where the "learn more" link at the foot of the aside points. */
  readonly helpUrl = 'https://support.hyland.com/access?dita:id=alfresco-content-services';

  readonly tabs: { id: DocTab; label: string; icon: string }[] = [
    { id: 'view', label: 'DMS.UI.412A94', icon: 'description' },
    { id: 'properties', label: 'DMS.UI.9BFE57', icon: 'settings' },
    { id: 'permissions', label: 'DMS.UI.FDF18F', icon: 'shield' },
    { id: 'history', label: 'DMS.UI.520E82', icon: 'schedule' }
  ];

  /** Toggled off/on to force the embedded viewer to re-fetch when content changes. */
  viewerVisible = true;

  /** Whether the summary button shows at all — set only when the feature is on. */
  readonly assistantStatus = this.assistant.status;

  /** The document's AI summary once asked for; null while it has not been. */
  summary: InsightResult | null = null;
  summaryBusy = false;

  /** Tags on the current node ({ entry: { id, tag } }). */
  tags: any[] = [];
  newTag = '';
  savingTag = false;

  /** The same actions ACA exposes in the file context menu (Move/Copy/Delete/…). */
  moreActions$: Observable<any[]> = of([]);

  /** Actions already available elsewhere (header buttons / tabs) — hidden from "more". */
  private readonly hiddenActionIds = new Set<string>([
    'app.context.menu.share', // → header link button (Share dialog)
    'app.context.menu.preview', // → "View" tab / fullscreen
    'app.context.menu.versions', // → HISTORY tab
    'app.context.menu.permissions' // → PERMISSIONS tab
  ]);

  ngOnInit(): void {
    // Reuse ACA's own context-menu actions for the "more" menu; they evaluate
    // against the current selection, which we set when a node loads. Drop the
    // ones already surfaced as header buttons or tabs to avoid duplicates.
    this.moreActions$ = this.appExtensions
      .getAllowedContextMenuActions()
      .pipe(map((actions) => actions.filter((action) => !this.hiddenActionIds.has(action?.id))));

    this.sub.add(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('id');
        if (id && id !== this.nodeId) {
          this.activeTab = 'view';
          this.load(id);
        }
      })
    );
    this.sub.add(
      this.route.queryParamMap.subscribe((q) => {
        this.explicitBack = !!q.get('back');
        this.backUrl = q.get('back') || '/all-documents';
      })
    );

    // Keep the Version badge / metadata current without a page reload after a
    // version is uploaded, reverted or deleted. Different ACA flows signal this
    // differently, so we listen to all of them:
    //  - nodeUpdated: emitted when a node's content/version changes
    //  - fileUploadComplete: the "Upload New Version" flow goes through the upload queue
    //  - REFRESH_PREVIEW: dispatched on revert / delete version
    this.sub.add(
      this.nodesApi.nodeUpdated.subscribe((node: any) => {
        if (node?.id && node.id === this.nodeId) {
          this.reloadNodeMeta(this.nodeId);
        }
      })
    );
    this.sub.add(
      this.uploadService.fileUploadComplete.subscribe(() => {
        if (this.nodeId) {
          this.reloadNodeMeta(this.nodeId);
        }
      })
    );
    this.sub.add(
      this.actions$.pipe(ofType('REFRESH_PREVIEW')).subscribe(() => {
        if (this.nodeId) {
          this.reloadNodeMeta(this.nodeId);
        }
      })
    );
  }

  private reloadNodeMeta(nodeId: string): void {
    const prevVersion = this.node?.properties?.['cm:versionLabel'];
    const prevModified = this.node?.modifiedAt;
    this.nodesApi.getNode(nodeId, { include: ['properties', 'aspectNames', 'path', 'allowableOperations', 'isFavorite'] }).subscribe({
      next: (node) => {
        if (this.nodeId === nodeId) {
          this.node = node;
          // If the content actually changed (new version), force the preview to
          // re-fetch by recreating the viewer.
          const contentChanged = node?.properties?.['cm:versionLabel'] !== prevVersion || String(node?.modifiedAt) !== String(prevModified);
          if (contentChanged) {
            this.reloadViewer();
          }
        }
      },
      error: () => {
        /* keep current data */
      }
    });
  }

  private reloadViewer(): void {
    this.viewerVisible = false;
    setTimeout(() => {
      this.viewerVisible = true;
    });
  }

  private load(nodeId: string): void {
    this.nodeId = nodeId;
    this.node = null;
    this.loadError = null;
    this.tags = [];
    this.newTag = '';
    this.summary = null;
    this.summaryBusy = false;
    this.nodesApi.getNode(nodeId, { include: ['properties', 'aspectNames', 'path', 'allowableOperations', 'isFavorite'] }).subscribe({
      next: (node) => {
        if (this.nodeId === nodeId) {
          this.node = node;
          // Make this node the active selection so ACA's actions (share, move,
          // delete, manage versions, …) operate on it.
          this.store.dispatch(new SetSelectedNodesAction([{ entry: node }] as any));
        }
      },
      error: (err) => {
        if (this.nodeId === nodeId) {
          const status = err?.status ?? err?.response?.status;
          this.loadError = status === 403 ? 'forbidden' : status === 404 ? 'missing' : 'failed';
        }
      }
    });
    this.loadTags(nodeId);
  }

  private loadTags(nodeId: string): void {
    this.tagService.getTagsByNodeId(nodeId).subscribe({
      next: (paging) => {
        if (this.nodeId === nodeId) {
          this.tags = paging?.list?.entries || [];
        }
      },
      error: () => {
        this.tags = [];
      }
    });
  }

  /** PDFs open in the studio; every other type keeps the ADF viewer. */
  get isPdf(): boolean {
    return this.node?.content?.mimeType === 'application/pdf';
  }

  /** Whether the reader may write — the studio's save button follows this. */
  get canEditPdf(): boolean {
    const ops = this.node?.allowableOperations;
    return !ops || ops.includes('update');
  }

  /** The studio saved a version — refresh the header/version metadata. */
  onPdfSaved(): void {
    if (this.nodeId) {
      this.reloadNodeMeta(this.nodeId);
    }
  }

  /** What kind of file this is, as the repository names it. */
  get mimeLabel(): string {
    return this.node?.content?.mimeTypeName || this.node?.content?.mimeType || '—';
  }

  get versionLabel(): string {
    const label = this.node?.properties?.['cm:versionLabel'];
    return label ? `v${label}` : '—';
  }

  addTag(): void {
    const name = this.newTag.trim();
    if (!name || !this.nodeId || this.savingTag) {
      return;
    }
    this.savingTag = true;
    const nodeId = this.nodeId;
    this.tagService.addTag(nodeId, name).subscribe({
      next: () => {
        this.newTag = '';
        this.savingTag = false;
        this.loadTags(nodeId);
      },
      error: () => {
        this.savingTag = false;
      }
    });
  }

  removeTag(tag: any): void {
    if (!this.nodeId || !tag?.entry?.id) {
      return;
    }
    const nodeId = this.nodeId;
    this.tagService.removeTag(nodeId, tag.entry.id).subscribe({
      next: () => this.loadTags(nodeId),
      error: () => {
        /* keep current list */
      }
    });
  }

  get nodeEntry(): any {
    return this.node ? { entry: this.node } : null;
  }

  get title(): string {
    return this.node?.properties?.['cm:title'] || '';
  }

  get typeIcon(): string {
    const mime: string = this.node?.content?.mimeType || '';
    if (mime.startsWith('image/')) {
      return 'image';
    }
    if (mime === 'application/pdf') {
      return 'picture_as_pdf';
    }
    if (mime.startsWith('video/')) {
      return 'movie';
    }
    if (mime.startsWith('audio/')) {
      return 'audiotrack';
    }
    if (mime.startsWith('text/')) {
      return 'description';
    }
    return 'insert_drive_file';
  }

  get breadcrumb(): string {
    return this.trail.map((part) => part.name).join(' › ');
  }

  /**
   * Opens the folder a trail segment names. The repository browser answers any
   * folder id — a document under Company Home or a shared area isn't a
   * "personal file", so this is the target that is always right.
   */
  openFolder(part: { id: string; name: string }): void {
    if (part.id) {
      void this.router.navigate(['/repository', part.id]);
    }
  }

  /** The current file name re-opens this page — kept clickable like the folders. */
  openSelf(): void {
    if (this.nodeId) {
      void this.router.navigate(['/document', this.nodeId]);
    }
  }

  /**
   * The trail getter returns fresh objects on every change-detection pass;
   * without a stable key *ngFor would destroy and recreate the crumb buttons
   * each time, detaching their listeners between a user's mousedown and click.
   */
  trackCrumb(_index: number, part: { id: string; name: string }): string {
    return part.id || part.name;
  }

  /** The folders the document sits under, as the header walks them. */
  get trail(): { id: string; name: string }[] {
    const elements = this.node?.path?.elements;
    if (!Array.isArray(elements)) {
      return [];
    }
    return elements.filter((element: any) => !!element?.name).map((element: any) => ({ id: element.id, name: element.name }));
  }

  get sizeLabel(): string {
    const bytes = this.node?.content?.sizeInBytes;
    if (bytes === undefined || bytes === null) {
      return '';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024;
      i++;
    }
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  initial(name?: string): string {
    return (name || '?').trim().charAt(0).toUpperCase();
  }

  /**
   * Arrow-key movement across the tab strip, as the tab pattern expects: only
   * the selected tab is in the tab order, so the arrows are the only way to
   * reach the other three from the keyboard.
   */
  onTabKeys(event: KeyboardEvent): void {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    const current = this.tabs.findIndex((tab) => tab.id === this.activeTab);

    let next: number;
    if (step !== undefined) {
      next = (current + step + this.tabs.length) % this.tabs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = this.tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activeTab = this.tabs[next].id;
    // the newly selected tab is the one now in the tab order, so focus follows
    setTimeout(() => document.getElementById(`aca-docview-tab-${this.activeTab}`)?.focus());
  }

  /** Opens ACA's Share dialog (creates / manages a public shared link). */
  share(): void {
    if (this.nodeEntry) {
      this.store.dispatch(new ShareNodeAction(this.nodeEntry));
    }
  }

  get isVersionable(): boolean {
    return !!this.node?.aspectNames?.includes('cm:versionable');
  }

  /**
   * Opens ACA's Manage Versions dialog. ACA's dialog crashes for files with no
   * version history (`entries[0].entry` on an empty list — a stock bug), which
   * happens for files that aren't versionable (e.g. system templates). For those
   * we just notify the user instead of opening the dialog.
   */
  manageVersions(): void {
    if (!this.nodeId || !this.node) {
      return;
    }
    if (this.isVersionable) {
      this.store.dispatch(new ManageVersionsAction(this.nodeEntry));
    } else {
      this.notification.showInfo(this.translate.instant('DMS.UI.73EFD5'));
    }
  }

  /**
   * Asks the assistant for a few lines on what this document says. The text it
   * reads is the indexed one — pages the caller may already open — and the
   * sidecar refuses outright when the feature is off or the level may not leave.
   */
  summarize(): void {
    if (!this.nodeId || this.summaryBusy) {
      return;
    }
    this.summaryBusy = true;
    this.assistant.summarize(this.nodeId).subscribe({
      next: (result) => {
        this.summaryBusy = false;
        if (result?.summary) {
          this.summary = result;
        } else {
          this.notification.showWarning(
            this.translate.instant(result?.reason === 'confidentiality' ? 'DMS.ASSISTANT.SUMMARY_WITHHELD' : 'DMS.ASSISTANT.SUMMARY_NO_TEXT')
          );
        }
      },
      error: (error) => {
        this.summaryBusy = false;
        this.notification.showError(this.translate.instant(error?.status === 403 ? 'DMS.ASSISTANT.SUMMARY_OFF' : 'DMS.ASSISTANT.SUMMARY_FAIL'));
      }
    });
  }

  /** Runs an ACA context-menu action against the current selection. */
  runAction(item: any): void {
    if (item?.actions?.click) {
      this.appExtensions.runActionById(item.actions.click);
    }
  }

  trackActionId(_index: number, item: any): string {
    return item?.id ?? String(_index);
  }

  /**
   * Prints through the watermark service: PDFs and images are stamped into a
   * copy when the administrator asked for it, anything else goes out plainly.
   */
  print(): void {
    if (!this.nodeId) {
      return;
    }
    const nodeId = this.nodeId;
    const mime = this.node?.content?.mimeType || '';
    const name = this.node?.name || '';
    void this.watermarkPrint.print(nodeId, mime, name, () => this.printRaw(nodeId));
  }

  /**
   * Downloads the indexed OCR text of this document as a Markdown file.
   *
   * The words were indexed per page when the file was uploaded (scans read by
   * the OCR engine, PDF pages that carried their own text indexed as-is), so
   * the .md mirrors that: one `##` heading per page for multi-page documents.
   * Files that were never read — Office documents, plain text — simply have
   * nothing indexed, which the notification says.
   */
  downloadText(): void {
    if (!this.nodeId) {
      return;
    }
    const nodeId = this.nodeId;
    this.ocr.indexedText(nodeId).subscribe({
      next: (doc) => {
        const pages = (doc?.pages ?? []).filter((p) => p.text?.trim());
        if (!pages.length) {
          this.notification.showInfo(this.translate.instant('DMS.UI.OCR_NO_TEXT'));
          return;
        }

        const name = doc.name || this.node?.name || nodeId;
        const multi = pages.length > 1;
        const markdown = [
          `# ${name}`,
          '',
          ...pages.map((p) => (multi ? `## ${this.translate.instant('DMS.UI.OCR_PAGE')} ${p.page}\n\n${p.text.trim()}` : p.text.trim()))
        ].join('\n\n');

        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${name.replace(/\.[^.]+$/, '')}.md`;
        link.click();
        URL.revokeObjectURL(link.href);
      },
      error: () => this.notification.showInfo(this.translate.instant('DMS.UI.OCR_NO_TEXT'))
    });
  }

  /** True while an OCR re-index is running — the menu item stays disabled. */
  reindexBusy = false;

  /** Whether this file kind can be (re)indexed — Word/text carry text Alfresco already extracts. */
  get canOcr(): boolean {
    return !!this.node?.name && this.ocrPipeline.kindOf(this.node.name) !== 'none';
  }

  /**
   * Rebuilds the OCR index for this document: fetches the repository content
   * back into a File and runs the same pipeline the upload page uses, so old
   * documents indexed before the feature existed (or read badly) get indexed
   * the same way new uploads are. The old index entries are dropped first —
   * fewer pages in the new version must not leave stale pages behind.
   */
  reindexOcr(): void {
    if (!this.nodeId || this.reindexBusy || !this.canOcr) {
      return;
    }
    const nodeId = this.nodeId;
    const name = this.node.name as string;

    this.reindexBusy = true;
    this.notification.showInfo(this.translate.instant('DMS.UI.OCR_REINDEX_START'));

    this.http.get(this.contentService.getContentUrl(nodeId, false), { responseType: 'blob' }).subscribe({
      next: async (blob) => {
        try {
          const file = new File([blob], name, { type: this.node?.content?.mimeType || blob.type || 'application/octet-stream' });
          await firstValueFrom(this.ocr.removeFromIndex(nodeId)).catch(() => false);
          const outcome = await firstValueFrom(this.ocrPipeline.readAndIndex(file, nodeId, name, () => {}));
          this.notification.showInfo(
            this.translate.instant('DMS.UI.OCR_REINDEX_DONE', { read: outcome.read + outcome.fromText, total: outcome.checked })
          );
        } catch {
          this.notification.showError(this.translate.instant('DMS.UI.OCR_REINDEX_FAIL'));
        } finally {
          this.reindexBusy = false;
        }
      },
      error: () => {
        this.reindexBusy = false;
        this.notification.showError(this.translate.instant('DMS.UI.OCR_REINDEX_FAIL'));
      }
    });
  }

  /** The unmarked print — the repository's content in its own window. */
  private printRaw(nodeId: string): void {
    const url = this.contentService.getContentUrl(nodeId, false);
    const win = window.open(url, '_blank');
    if (win) {
      win.addEventListener('load', () => {
        try {
          win.focus();
          win.print();
        } catch {
          /* the new tab still shows the content for manual printing */
        }
      });
    }
  }

  openNative(): void {
    if (this.nodeId) {
      this.redirect.openNative(this.nodeId, this.backUrl);
    }
  }

  /** Hands this document to the upload page's update mode — a new version, not a new document. */
  updateDocument(): void {
    if (this.nodeId) {
      void this.router.navigate(['/upload'], { queryParams: { update: this.nodeId } });
    }
  }

  /**
   * Back to where the person came from. Only the list views pass `?back=`;
   * from anywhere else in the app — a task, the dashboard, a search — the
   * previous page is the one in history. Opened from a link with nothing
   * before it, All documents is the sensible place to land.
   */
  close(): void {
    if (this.explicitBack) {
      this.router.navigateByUrl(this.backUrl);
    } else if (this.router.lastSuccessfulNavigation?.previousNavigation) {
      this.location.back();
    } else {
      this.router.navigateByUrl(this.backUrl);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    // Clear the selection we set, so leaving the page doesn't leave a node
    // "selected" in the rest of the app.
    this.store.dispatch(new ResetSelectionAction());
  }
}
